/**
 * VÉRAULT Travels — Email Confirmation Parser (Phase 5)
 *
 * Modal that lets the advisor paste a raw confirmation email and get a structured booking
 * proposal back from /api/ai/parse-email.  Human stays in the loop: the proposal is shown
 * in editable form, advisor reviews, then "Create booking" hits the existing POST endpoint.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailParserModal({ open, onClose, onCreated, trips = [], clients = [] }) {
  const [step, setStep] = useState('paste');           // paste | review
  const [text, setText] = useState('');
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setStep('paste'); setText(''); setProposal(null); setLoading(false); setSaving(false);
  };
  const handleClose = () => { reset(); onClose(); };

  const parse = async () => {
    if (!text.trim()) {
      toast.error('Paste an email first');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/parse-email`, { text }, { withCredentials: true });
      if (!res.data || (res.data.confidence ?? 0) < 0.2) {
        toast.error("That doesn't look like a travel confirmation. Try another email.");
        return;
      }
      setProposal(res.data);
      setStep('review');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Parser unavailable');
    } finally {
      setLoading(false);
    }
  };

  const update = (k, v) => setProposal(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!proposal?.trip_id) {
      toast.error('Pick a trip first');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        trip_id: proposal.trip_id,
        client_id: proposal.client_id || null,
        booking_type: proposal.booking_type || 'other',
        vendor: proposal.vendor || '',
        confirmation_number: proposal.confirmation_number || '',
        start_date: proposal.start_date || null,
        end_date: proposal.end_date || null,
        quoted_cost: Number(proposal.quoted_cost) || 0,
        actual_cost: Number(proposal.quoted_cost) || 0,
        currency: proposal.currency || 'USD',
        description: proposal.description || '',
        notes: proposal.notes || '',
        reservation_status: 'confirmed',
      };
      await axios.post(`${API}/bookings`, payload, { withCredentials: true });
      toast.success('Booking created from email');
      onCreated?.();
      handleClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C8A85A]" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C8C8C]">AI assist</p>
              <h2 className="font-serif text-xl text-[#1A1A1A]">Parse confirmation email</h2>
            </div>
          </div>
          <button type="button" onClick={handleClose} className="text-[#999] hover:text-[#1A1A1A]" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 'paste' && (
          <div className="space-y-3" data-testid="email-parser-paste-step">
            <p className="text-xs text-[#8C8C8C] leading-relaxed">
              Paste the full body of a booking confirmation email below. The model extracts
              vendor, dates, cost, traveler names, and the confirmation number. You'll review
              everything before it saves.
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste email body…"
              rows={12}
              data-testid="email-parser-text"
              className="w-full px-3 py-2 text-sm rounded border border-[#E8E4DC] bg-white focus:outline-none focus:border-[#1A1A1A]"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={handleClose} className="px-4 py-2 text-xs uppercase tracking-wider text-[#6B6B6B]">Cancel</button>
              <button
                type="button"
                onClick={parse}
                disabled={loading}
                data-testid="email-parser-parse"
                className="px-4 py-2 text-xs uppercase tracking-wider bg-[#1A1A1A] text-white rounded hover:bg-black disabled:opacity-50"
              >
                {loading ? 'Parsing…' : 'Parse email'}
              </button>
            </div>
          </div>
        )}

        {step === 'review' && proposal && (
          <div className="space-y-3" data-testid="email-parser-review-step">
            <p className="text-[10px] uppercase tracking-wider text-[#C8A85A]">
              Confidence {Math.round((proposal.confidence || 0) * 100)}%
            </p>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <Field label="Booking type">
                <input value={proposal.booking_type || ''} onChange={(e) => update('booking_type', e.target.value)} className="w-full px-2 py-1.5 border border-[#E8E4DC] rounded text-sm" />
              </Field>
              <Field label="Vendor">
                <input value={proposal.vendor || ''} onChange={(e) => update('vendor', e.target.value)} className="w-full px-2 py-1.5 border border-[#E8E4DC] rounded text-sm" />
              </Field>
              <Field label="Confirmation #">
                <input value={proposal.confirmation_number || ''} onChange={(e) => update('confirmation_number', e.target.value)} className="w-full px-2 py-1.5 border border-[#E8E4DC] rounded text-sm" />
              </Field>
              <Field label="Cost">
                <div className="flex gap-1">
                  <input type="number" value={proposal.quoted_cost || ''} onChange={(e) => update('quoted_cost', e.target.value)} className="flex-1 px-2 py-1.5 border border-[#E8E4DC] rounded text-sm" />
                  <input value={proposal.currency || 'USD'} onChange={(e) => update('currency', e.target.value)} className="w-16 px-2 py-1.5 border border-[#E8E4DC] rounded text-sm" />
                </div>
              </Field>
              <Field label="Start">
                <input type="date" value={proposal.start_date || ''} onChange={(e) => update('start_date', e.target.value)} className="w-full px-2 py-1.5 border border-[#E8E4DC] rounded text-sm" />
              </Field>
              <Field label="End">
                <input type="date" value={proposal.end_date || ''} onChange={(e) => update('end_date', e.target.value)} className="w-full px-2 py-1.5 border border-[#E8E4DC] rounded text-sm" />
              </Field>
              <Field label="Trip *" className="col-span-2">
                <select value={proposal.trip_id || ''} onChange={(e) => update('trip_id', e.target.value)} data-testid="email-parser-trip" className="w-full px-2 py-1.5 border border-[#E8E4DC] rounded text-sm">
                  <option value="">— Select trip —</option>
                  {trips.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.internal_trip_name || t.client_trip_title || t.destinations || t.id}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Client" className="col-span-2">
                <select value={proposal.client_id || ''} onChange={(e) => update('client_id', e.target.value)} className="w-full px-2 py-1.5 border border-[#E8E4DC] rounded text-sm">
                  <option value="">— Optional client link —</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name || c.email}</option>
                  ))}
                </select>
              </Field>
              <Field label="Description" className="col-span-2">
                <input value={proposal.description || ''} onChange={(e) => update('description', e.target.value)} className="w-full px-2 py-1.5 border border-[#E8E4DC] rounded text-sm" />
              </Field>
            </div>

            {proposal.traveler_names?.length > 0 && (
              <p className="text-xs text-[#8C8C8C]">Travelers detected: {proposal.traveler_names.join(', ')}</p>
            )}

            <div className="flex justify-between gap-2 pt-2 border-t border-[#E8E4DC]">
              <button type="button" onClick={() => setStep('paste')} className="text-xs uppercase tracking-wider text-[#6B6B6B] hover:text-[#1A1A1A]">← Re-parse</button>
              <div className="flex gap-2">
                <button type="button" onClick={handleClose} className="px-4 py-2 text-xs uppercase tracking-wider text-[#6B6B6B]">Cancel</button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="email-parser-save"
                  className="px-4 py-2 text-xs uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white rounded disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Create booking'}
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">{label}</span>
      {children}
    </label>
  );
}
